import React, { forwardRef } from 'react';
import Icon, { IconName, IconSize } from '../Icon';
import { Shadow, colors, shadows } from '../../../utils/theme';
import Stack from '../Stack';
import { GestureResponderEvent, LayoutChangeEvent, StyleProp, View, ViewStyle, Pressable } from 'react-native';
import { TextColor } from '../Text';
import { StateStyles, useInteractionStateStyles } from '../../../hooks/useInteractionStateStyles';

export type IconButtonVariant =
    | 'default'
    | 'minimal'
    | 'transparent'
    | 'large-transparent'
    | 'invert'
    | 'filled'
    | 'header-back-button'
    | 'small-record-button'
    | 'large-record-button'
    | 'audio-play-pause-button';

export type IconButtonOwnProps = {
    icon: IconName;
    isDisabled?: boolean;
    onPress: (e: GestureResponderEvent) => void;
    testID?: string;
    variant?: IconButtonVariant;
    onLayout?: (event: LayoutChangeEvent) => void;
    strokeWidth?: number;
    style?: StyleProp<ViewStyle>;
    customSize?: IconSize;
};

export type IconButtonProps = IconButtonOwnProps;

type StateStyle = {
    background: string;
    text: TextColor;
    borderColor: string;
    shadow?: Shadow;
};
const buttonStateStyles: Record<IconButtonVariant, StateStyles<StateStyle>> = {
    default: {
        default: {
            background: colors['Secondary-Button-Default'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
            shadow: shadows.default,
        },
        hovered: {
            background: colors['Secondary-Button-Hover'],
            text: 'Gray-Text',
            borderColor: colors['Secondary-Button-Hover-Border'],
            shadow: shadows.hover,
        },
        pressed: {
            background: colors['Secondary-Button-Press'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
            shadow: shadows.press,
        },
        inactive: {
            background: colors['Secondary-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Grey-Light'],
        },
    },
    minimal: {
        default: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        hovered: {
            background: colors['Secondary-Button-Hover'],
            text: 'Gray-Text',
            borderColor: colors['Secondary-Button-Hover-Border'],
        },
        pressed: {
            background: colors['Secondary-Button-Press'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
        },
        inactive: {
            background: colors['Secondary-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Grey-Light'],
        },
    },
    'header-back-button': {
        default: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        hovered: {
            background: colors['Secondary-Button-Hover'],
            text: 'Gray-Text',
            borderColor: colors['Secondary-Button-Hover-Border'],
        },
        pressed: {
            background: colors['Secondary-Button-Press'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
        },
        inactive: {
            background: colors['Secondary-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Grey-Light'],
        },
    },
    transparent: {
        default: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        hovered: {
            background: 'transparent',
            text: 'Gray-Text',
            borderColor: 'transparent',
        },
        pressed: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        inactive: {
            background: 'transparent',
            text: 'Inactive-Text',
            borderColor: 'transparent',
        },
    },
    'large-transparent': {
        default: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        hovered: {
            background: 'transparent',
            text: 'Gray-Text',
            borderColor: 'transparent',
        },
        pressed: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        inactive: {
            background: 'transparent',
            text: 'Inactive-Text',
            borderColor: 'transparent',
        },
    },
    invert: {
        default: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        hovered: {
            background: 'transparent',
            text: 'Gray-Text',
            borderColor: 'transparent',
        },
        pressed: {
            background: 'transparent',
            text: 'Dark-Text',
            borderColor: 'transparent',
        },
        inactive: {
            background: 'transparent',
            text: 'Inactive-Text',
            borderColor: 'transparent',
        },
    },
    filled: {
        default: {
            background: colors['Black'],
            text: 'White',
            borderColor: colors['Black'],
        },
        hovered: {
            background: colors['Grey'],
            text: 'Grey-Light',
            borderColor: colors['Grey'],
        },
        pressed: {
            background: colors['Grey'],
            text: 'Grey-Light',
            borderColor: colors['Grey'],
        },
        inactive: {
            background: colors['Grey'],
            text: 'Grey-Light',
            borderColor: colors['Grey'],
        },
    },
    'small-record-button': {
        default: {
            background: colors['Alert-Button-Default'],
            text: 'White-Text',
            borderColor: colors['Alert-Button-Default'],
        },
        hovered: {
            background: colors['Alert-Button-Hover'],
            text: 'White-Text',
            borderColor: colors['Alert-Button-Hover'],
        },
        pressed: {
            background: colors['Alert-Button-Press'],
            text: 'White-Text',
            borderColor: colors['Alert-Button-Press'],
        },
        inactive: {
            background: colors['Alert-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Alert-Button-Inactive'],
        },
    },
    'large-record-button': {
        default: {
            background: colors['Alert-Button-Default'],
            text: 'White-Text',
            borderColor: colors['Alert-Button-Default'],
        },
        hovered: {
            background: colors['Alert-Button-Hover'],
            text: 'White-Text',
            borderColor: colors['Alert-Button-Hover'],
        },
        pressed: {
            background: colors['Alert-Button-Press'],
            text: 'White-Text',
            borderColor: colors['Alert-Button-Press'],
        },
        inactive: {
            background: colors['Alert-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Alert-Button-Inactive'],
        },
    },
    'audio-play-pause-button': {
        default: {
            background: colors['Primary-Button-Default'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
        },
        hovered: {
            background: colors['Primary-Button-Hover'],
            text: 'Gray-Text',
            borderColor: colors['Secondary-Button-Hover-Border'],
        },
        pressed: {
            background: colors['Primary-Button-Press'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
        },
        inactive: {
            background: colors['Primary-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Grey-Light'],
        },
    },
};

const styleMap: Record<IconButtonVariant, (borderColor?: string) => StyleProp<ViewStyle>> = {
    default: (borderColor?: string) => ({
        borderRadius: 5,
        width: 40,
        height: 40,
        borderColor: borderColor,
        borderWidth: borderColor ? 1 : 0,
    }),
    minimal: () => ({
        width: 24,
        height: 24,
    }),
    'header-back-button': () => ({
        padding: 8,
        width: 40,
        height: 40,
    }),
    transparent: () => ({
        width: 24,
        height: 24,
    }),
    'large-transparent': () => ({
        width: 40,
        height: 40,
    }),
    invert: () => ({
        width: 24,
        height: 24,
    }),
    filled: () => ({
        height: 24,
        width: 24,
        borderRadius: 12,
    }),
    'small-record-button': () => ({
        borderRadius: 16,
        width: 32,
        height: 32,
    }),
    'large-record-button': () => ({
        borderRadius: 40,
        width: 80,
        height: 80,
    }),
    'audio-play-pause-button': () => ({
        borderRadius: 24,
        width: 48,
        height: 48,
    }),
};

const IconButton: React.FC<IconButtonProps> = forwardRef((props, ref) => {
    const { styles, renderInHoverContainer, onPressIn, onPressOut } = useInteractionStateStyles({
        isDisabled: props.isDisabled,
        stateStyles: buttonStateStyles[props.variant ?? 'default'],
    });

    const getIconSize = (): IconSize => {
        if (props.customSize) {
            return props.customSize;
        } else if (props.variant === 'minimal') {
            return 'minimal-button-icon';
        } else if (props.variant === 'header-back-button') {
            return 'header-back-button';
        } else if (props.variant === 'large-record-button') {
            return 'large-record-button';
        } else if (props.variant === 'audio-play-pause-button') {
            return 'folder-picker-icon';
        } else {
            return 'button-icon';
        }
    };

    return renderInHoverContainer(
        <View ref={ref as any}>
            <Pressable
                style={[
                    {
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: styles.background,
                        ...(styleMap[props.variant ?? 'default'](styles.borderColor) as any),
                    },
                    props.style,
                ]}
                disabled={props.isDisabled}
                onPress={props.onPress}
                onPressIn={onPressIn}
                onPressOut={onPressOut}
                testID={props.testID}
                onLayout={props.onLayout}
            >
                <Stack horizontal align="center" justify="center" space={2.5}>
                    <Icon size={getIconSize()} name={props.icon} color={styles.text} strokeWidth={props.strokeWidth} />
                </Stack>
            </Pressable>
        </View>,
    );
});

export default React.memo(IconButton);
